'use client';

import React from 'react';
import clsx from 'clsx';
import { useCookies } from 'next-client-cookies';
import Skeleton from 'react-loading-skeleton';
import Link from 'components/commercetools-ui/atoms/link';
import { useAccountTabs } from 'helpers/account';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { resolveTextConfiguration } from 'helpers/hooks/useResolvedLocalizedObject';
import { mediumDesktop } from 'helpers/utils/screensizes';
import { AccountTabsDataSource } from '../../account/types/types';

interface Props {
  text: AccountTabsDataSource;
  hash: string;
  accountLoading?: boolean;
  showSidebarNames: boolean;
  setShowSidebarNames: (val: boolean) => void;
}

const AccountTabsComponent: React.FC<Props> = ({
  text,
  hash,
  accountLoading,
  showSidebarNames,
  setShowSidebarNames,
}) => {
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const [isDesktopSize] = useMediaQuery(mediumDesktop);
  const accountTabs = resolveTextConfiguration(text.dataSource.config.textConfiguration, locale);
  const accountTabsText = accountTabs?.[0] ?? {};

  const tabs = useAccountTabs({
    text: accountTabsText,
    hash,
    emptyPageSubTitle: {
      orderStatus: '',
      addressBook: '',
      paymentMethods: '',
      accountDashboard: '',
      myPhotos: '',
    },
    isDesktopSize
  });

  return (
    <aside
      className="hidden self-start transition-all duration-300 md:block"
      style={{ width: '161px' }}
      onMouseEnter={() => setShowSidebarNames(true)}
      onMouseLeave={() => setShowSidebarNames(false)}
      data-ffid="ctr-acct-tabs"
    >
      <div className="flex flex-col items-end gap-16" data-ffid="ctr-acct-tabs-wrapper">
        <div
          className={clsx(
            'flex w-min flex-col gap-8 bg-[#F8F9FA] p-8 transition-all duration-300',
            showSidebarNames ? 'rounded-xl' : 'rounded-[56px]',
          )}
          data-ffid="ctr-acct-tabs-container"
        >
          {tabs.map((tab) => (
            <Link
              link={`/account${tab.href}`}
              key={tab.name}
              data-ffid={`btn-acc-tab-${tab.name}`}
              className={clsx(
                'group flex items-center transition-all duration-300',
                showSidebarNames ? 'max-w-min gap-4 rounded-full p-6' : 'justify-center rounded-full p-4',
                tab.isActive ? 'bg-[#01916D] text-white' : 'text-[#333333] hover:bg-[#e6f4f3] hover:text-[#018463]',
              )}
            >
              <div className="shrink-0 px-3 transition-transform duration-300 group-hover:scale-110">
                {accountLoading ? <Skeleton circle width={24} height={24} /> : tab.icon}
              </div>
              <div
                className={clsx(
                  'overflow-hidden transition-all duration-300',
                  showSidebarNames ? 'max-w-200 opacity-100' : 'max-w-0 opacity-0',
                )}
              >
                <span className="mr-12 whitespace-nowrap pl-4 text-14 font-medium">{tab?.sidebarLabel}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </aside>
  );
};

export default AccountTabsComponent;
